import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import RecentVideosCarousel from '../components/RecentVideosCarousel';
import PromptInputTabs from '../components/PromptInputTabs';
import SubscriptionModal from '../components/SubscriptionModal';
import RightSideMenu from '../components/RightSideMenu';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CreditStatus from '../components/CreditStatus';
import { authService } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import logoImage from '../assets/animlearn-logo.png';
import { getCreditStatus } from '../services/api';
import { Video, Menu, ArrowLeft, Sparkles, Globe, Zap, AlertTriangle, CheckCircle } from 'lucide-react';
import './ContentCreator.css';

function ContentCreator() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showRightMenu, setShowRightMenu] = useState(false);
  const creditStatusRef = useRef(null);
  const user = authService.getUser();
  const [creditStatus, setCreditStatus] = useState(null);
  
  // Wrap loadCreditStatus in useCallback to fix the useEffect dependency warning
  const loadCreditStatus = useCallback(async () => {
    try {      
      const status = await getCreditStatus();
      setCreditStatus(status);
    } catch (err) {
      console.error('Error loading credit status:', err);
    }
  }, []);

  useEffect(() => {
    loadCreditStatus();
  }, [loadCreditStatus]); // Now we can safely include loadCreditStatus

  const handleVideoGenerated = () => {
    if (creditStatusRef.current) {
      creditStatusRef.current.refresh();
    }
    setRefreshKey(prev => prev + 1);
  };

  const handleOpenSubscription = () => {
    setShowSubscriptionModal(true);
    document.body.classList.add('modal-open');
  };

  const handleCloseSubscription = () => {
    setShowSubscriptionModal(false);
    document.body.classList.remove('modal-open');
  };

  const handleSelectPlan = (plan) => {
    console.log('Selected plan:', plan);
  };

  return (
    <div className="content-creator">
      {/* Header */}
      <header className="creator-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo" onClick={() => navigate('/')}>
              <img src={logoImage} alt="AnimLearn" className="logo-image" />
            </div>
          </div>
          
          <div className="header-right">
            {/* Compact Credit Status in Header */}
            <CreditStatus 
              ref={creditStatusRef}
              compact={true}
              onUpgrade={handleOpenSubscription}
            />
            
            <LanguageSwitcher />
            
            {/* Hamburger Menu Button */}
            <button 
              onClick={() => setShowRightMenu(true)} 
              className="btn-menu"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="creator-main">
        <div className="creator-container">
          {/* Back Button */}
          <div className="creator-back">
            <button onClick={() => navigate('/dashboard')} className="back-link">
              <ArrowLeft size={20} />
              <span>{t.backToDashboard || 'Back to Dashboard'}</span>
            </button>
          </div>

          {/* Enhanced Page Header with All Info */}
          <div className="creator-page-header">
            <div className="header-content-wrapper">
              <div className="header-icon-section">
                <div className="creator-icon">
                  <Sparkles size={56} />
                </div>
              </div>
              
              <div className="header-text-section">
                <h1 className="page-title">{t.contentCreator?.title || 'Content Creator'}</h1>
                <p className="page-subtitle">
                  {t.contentCreator?.subtitle || 'Create educational videos with AI-powered animations for Chemistry, Physics, and Mathematics'}
                </p>
                
                <div className="header-features">
                  <div className="feature-badge">
                    <Video size={16} />
                    <span>{t.contentCreator?.feature1 || 'Animated Explanations'}</span>
                  </div>
                  <div className="feature-badge">
                    <Globe size={16} />
                    <span>{t.contentCreator?.feature2 || 'Multi-language Support'}</span>
                  </div>
                  <div className="feature-badge">
                    <Zap size={16} />
                    <span>{t.contentCreator?.feature3 || 'Fast Generation'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Responsibility Notice */}
            <div className="responsibility-notice">
              <div className="notice-icon">
                <AlertTriangle size={24} />
              </div>
              <div className="notice-content">
                <h3 className="notice-title">
                  {t.contentCreator?.responsibilityTitle || 'Create with Accuracy & Responsibility'}
                </h3>
                <p className="notice-text">
                  {t.contentCreator?.responsibilityText || 'The quality of your video depends on the accuracy of your input. Please provide correct solutions and explanations. Our AI assists with visualization and animation, but you are responsible for ensuring the educational content is accurate. Students and educators rely on your due diligence.'}
                </p>
                <div className="notice-points">
                  <div className="notice-point">
                    <CheckCircle size={18} />
                    <span>{t.contentCreator?.point1 || 'Verify your solutions before generating'}</span>
                  </div>
                  <div className="notice-point">
                    <CheckCircle size={18} />
                    <span>{t.contentCreator?.point2 || 'Double-check formulas and calculations'}</span>
                  </div>
                  <div className="notice-point">
                    <CheckCircle size={18} />
                    <span>{t.contentCreator?.point3 || 'Review generated content for accuracy'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Create Section */}
          <section className="create-section">
            <div className="create-content">
              <PromptInputTabs onVideoGenerated={handleVideoGenerated} />
            </div>
          </section>

          {/* Recent Videos Carousel Section */}
          <section className="videos-section">
            <div className="section-header-main">
              <div className="section-title-group">
                <h2 className="section-title">{t.recentVideos || 'Recent Videos'}</h2>
                <p className="section-subtitle">{t.recentVideosDesc || 'Your recently created videos'}</p>
              </div>
              <button 
                onClick={() => navigate('/videos')} 
                className="btn-view-all-videos"
              >
                <Video size={18} />
                <span>{t.viewAllVideos || 'View All Videos'}</span>
              </button>
            </div>
            <div className="videos-content">
              <RecentVideosCarousel key={refreshKey} />
            </div>
          </section>
        </div>
      </main>

      {/* Right Side Menu */}
      <RightSideMenu
        isOpen={showRightMenu}
        onClose={() => setShowRightMenu(false)}
        user={user}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onOpenSubscription={handleOpenSubscription}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={handleCloseSubscription}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onSelectPlan={handleSelectPlan}
      />
    </div>
  );
}

export default ContentCreator;